import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { useProjectNavigate } from "../../../common/utils/useProjectNavigate";
import { useDispatch, useSelector } from "react-redux";
import {
  setIsOpenProfile,
  setProfileUser,
  selectIsOpenProfile,
  upsertUser,
  removeUsers,
} from "../../../store/slices/user_management";
import "./styles.css";
import getDateByDiff from "../../../common/utils/calendar/get-date-by-diff";
import UserActivityStatus from "../../../components/user-activity-status/user-activity-status";
import InfinityTable from "../../../components/infinity-table/infinity-table";
import useSearch from "./useSearch";
import useUsers from "./useUsers";
import InviteUserModal from "../../../components/modals/invitations/user/invite-user";
import { Body } from "../../../components/main-body/body";
import { TableCell } from "../../../components/table/table-cell/table-cell";
// import { TableCellChips } from "../../../components/table/table-cell/table-cell-chips";
// import classNames from "classnames";
import DropdownFilters from "../../../components/dropdown-filters/dropdown-filters";
import { selectSettings } from "../../../store/slices/settings";
import IconCheckboxViolet from "../../../components/icon/icons/checkbox-violet";
import ApiUsersManagement from "../../../api/user-manager";
import { useBulkSelection } from "../../../common/utils/useBulkSelection";
import BulkSelectionBar from "../../../components/table/bulk-selection-bar";
import useNotification from "../../../components/notification/notification";
import useAdminEvents from "../../../hooks/useAdminEvents";
import { useCan } from "../../../common/utils/usePermissions";
import { useAssignableRoles } from "../../../components/modals/invitations/user/role-options";

const UserManagament = ({ status, type, scope = "organization" }) => {
  const { openNotification } = useNotification();
  const dispatch = useDispatch();
  const settings = useSelector(selectSettings);
  const sitesEnabled =
    settings?.find((s) => s.key === "sitesEnable")?.value === true;
  const { pathname, search: locationSearch } = useLocation();
  const { search, handleUpdateSearchValue } = useSearch();
  const [isOpen, setOpen] = useState(false);
  const { id } = useParams();
  // LF360-2045: a role's details link here with ?projectRole=<role id>, to list
  // the project's members holding that role. Read once, as the starting filter.
  const [initialFilters] = useState(() => {
    const role = new URLSearchParams(locationSearch).get("projectRole");
    return scope === "project" && role ? { roles: [role] } : {};
  });
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState({ field: null, order: null });
  const currentProject = useSelector((state) => state.projects.current);
  const currentProjectId = currentProject?.id || null;
  const [isBulkDeleting, setBulkDeleting] = useState(false);
  // LF360-2038: routes say "organization", this page used to test for
  // "organisation" — so Organization users got the project page's columns and
  // role filter.
  const isOrgScope = scope !== "project";
  // LF360-2024: organization users are Organization admins' only, who hold
  // every permission; in a project these are the project's own.
  const canInvite = useCan("users.invite");
  const canDelete = useCan("users.delete");

  // LF360-2024: the role as it is named now — custom ones included — with the
  // built-in key for the chip.
  const getProjectRoleForUser = (user) => {
    if (user.role === "admin" || user.role === "systemAdmin") {
      return { status: user.role };
    }

    if (!currentProjectId) return { status: null };

    const link = (user.UserProjects || []).find(
      (up) => up.projectId === currentProjectId
    );

    return { status: link?.roleKey || link?.role || "user", text: link?.roleName };
  };

  // The roles to filter by: those the signed-in user may give (all of them
  // for an organization admin). Filtered by id: a custom role has no key.
  const assignableRoles = useAssignableRoles(scope === "project");

  const apiFilters = useMemo(() => {
    if (scope === "project") {
      const { roles, ...rest } = filters || {};
      return {
        ...rest,
        projectRoles: roles,
      };
    }
    return filters;
  }, [filters, scope]);

  const { users, hasMore, isLoading, fetchNext, resetAndFetch } = useUsers({
    search,
    filters: apiFilters,
    sort,
    scope,
    status,
    projectId: scope === "project" ? currentProjectId : null,
  });

  const { selectedKeys, clearSelection, getSelectionColumn, setSelectedKeys } =
    useBulkSelection(users, { rowKey: "id" });

  const tableRef = useRef(null);
  const [bulkLeft, setBulkLeft] = useState(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!tableRef.current) return;
      const rect = tableRef.current.getBoundingClientRect();
      setBulkLeft(rect.left + rect.width / 2);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  const navigate = useProjectNavigate();
  const location = useLocation();
  const [sitesList, setSitesList] = useState([]);
  const isOpenProfile = useSelector(selectIsOpenProfile);

  const handleToogleModal = () => {
    if (isOpen) {
      resetAndFetch();
    }
    setOpen(!isOpen);
  };

  useEffect(() => {
    if (!isOpenProfile && id) {
      const basePath = pathname.replace(/\/[^/]+$/, "");
      handleNavigate(basePath);
    }
  }, [isOpenProfile, id, pathname]);

  useEffect(() => {
    if (id && users?.length) {
      const user = users.find((user) => user.id === id);
      if (user) {
        dispatch(setProfileUser(user));
        dispatch(setIsOpenProfile(true));
      }
    }
  }, [id, users, dispatch]);

  useEffect(() => {
    if (!id) {
      dispatch(setIsOpenProfile(false));
      dispatch(setProfileUser(null));
    }
  }, [id, dispatch]);

  // Every site of this project for the filter, not the first twenty.
  useEffect(() => {
    if (isOrgScope || !currentProjectId) return;

    ApiUsersManagement.getProjectSites([currentProjectId])
      .then(({ data }) => {
        const project = (data?.projects || [])[0];
        setSitesList((project?.sites || []).map((s) => ({ id: s.id, name: s.name })));
      })
      .catch(() => setSitesList([]));
  }, [isOrgScope, currentProjectId]);

  const { t } = useTranslation();

  useAdminEvents({
    onUserChanged: (payload) => {
      if (payload?.action === "updated" && payload?.user) {
        dispatch(upsertUser(payload.user));
        return;
      }

      if (payload?.action === "deleted" || payload?.action === "bulk_deleted") {
        dispatch(removeUsers(payload?.deletedIds || [payload?.id].filter(Boolean)));
        return;
      }

      resetAndFetch();
    },
  });

  const handleNavigate = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const openUserProfile = (user) => {
    const basePath = id
      ? pathname.replace(/\/[^/]+$/, "")
      : pathname.replace(/\/$/, "");

    const pathWithId = `${basePath}/${user.id}`;

    dispatch(setProfileUser(user));
    dispatch(setIsOpenProfile(true));
    handleNavigate(pathWithId);
  };

  const headerTitle = useMemo(() => {
    switch (pathname) {
      case "/administration/disabled-users":
        return t("user_management.title_disabled");
      case "/administration/pending-users":
        return t("user_management.title_pending");
      default:
        return t("user_management.title");
    }
  }, [pathname, t, type]);

  const tableColumns =
    isOrgScope
      ? [
          {
            title: "Name / Email",
            dataIndex: "nameAndEmail",
            key: "nameAndEmail",
            sorter: true,
            width: "30%",
            render: (_, user) => (
              <div
                className="lf-table-cell"
                style={{ display: "flex", alignItems: "flex-start" }}
              >
                <TableCell
                  img={user.avatarUrl || " "}
                  title={`${user.firstName} ${user.lastName}`}
                  text={user.email}
                />
              </div>
            ),
          },

          {
            title: "Organization admin",
            key: "orgAdmin",
            width: "20%",
            render: (_, user) => {
              if (user.role === "systemAdmin") {
                return (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      color: "#A967FF",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <IconCheckboxViolet />
                    <span>SA</span>
                  </span>
                );
              }

              if (user.role === "admin") {
                return (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconCheckboxViolet />
                  </span>
                );
              }

              return <span>—</span>;
            },
          },
          {
            title: "Projects",
            key: "projects",
            width: "15%",
            render: (_, user) => {
              if (user.role === "admin" || user.role === "systemAdmin") {
                return "All";
              }

              const count = (user.UserProjects || []).length;
              if (!count) return "0 projects";

              return `${count} project${count === 1 ? "" : "s"}`;
            },
          },

          {
            title: "Last active",
            sorter: true,
            dataIndex: "lastActivity",
            key: "lastActivity",
            width: "15%",
            render: (_, user) => (
              <TableCell text={getDateByDiff(user.lastActivity || user.invitedAt)} />
            ),
          },

          {
            title: "Status",
            sorter: true,
            dataIndex: "status",
            key: "status",
            width: "10%",
            render: (_, user) => (
              <div className="lf-table-cell">
                <UserActivityStatus userStatus={user.status} />
              </div>
            ),
          },
        ]
      : [
          {
            title: t("user_management.table.name"),
            sorter: true,
            dataIndex: "name",
            key: "name",
            width: "30%",
            render: (_, user) => (
              <TableCell
                img={user.avatarUrl || " "}
                title={`${user.firstName} ${user.lastName}`}
              />
            ),
          },
          {
            title: t("user_management.table.email"),
            sorter: true,
            dataIndex: "email",
            key: "email",
            width: "15%",
            render: (_, user) => <TableCell text={user.email || "N/A"} />,
          },

          {
            title: t("user_management.table.projectRole"),
            // sorter: true,
            dataIndex: "role",
            key: "role",
            width: "10%",
            render: (_, user) => {
              const projectRole = getProjectRoleForUser(user);

              return (
                <div className="lf-table-cell">
                  <UserActivityStatus userStatus={projectRole.status} text={projectRole.text} />
                </div>
              );
            },
          },
          ...(sitesEnabled
            ? [
                {
                  title: t("user_management.table.sites"),
                  dataIndex: "sites",
                  key: "sites",
                  width: "20%",
                  // sorter: true,
                  render: (_, user) => (
                    <TableCell
                      text={
                        (user.Sites || [])
                          .map((s) => s.name)
                          .join(", ") || "N/A"
                      }
                    />
                  ),
                },
              ]
            : []),
          {
            title: t("user_management.table.last_active"),
            sorter: true,
            dataIndex: "lastActivity",
            key: "lastActivity",
            width: "15%",
            render: (_, user) => (
              <TableCell text={getDateByDiff(user.lastActivity || user.invitedAt)} />
            ),
          },
          {
            title: t("user_management.table.status"),
            sorter: true,
            dataIndex: "status",
            key: "status",
            render: (_, user) => (
              <div className="lf-table-cell">
                <UserActivityStatus userStatus={user.status} />
              </div>
            ),
          },
        ];

  const headerProps = {
    title: headerTitle,
    search: { value: search, onHandleChange: handleUpdateSearchValue },
    create: canInvite ? {
      label: t("user_management.btn.invite"),
      onClick: handleToogleModal,
      renderModalComponent: (
        <InviteUserModal
          isOpen={isOpen}
          close={handleToogleModal}
          scope={isOrgScope ? "organization" : "project"}
          projectId={scope === "project" ? currentProjectId : undefined}
        />
      ),
    } : undefined,
  };

  const handleTableChange = (_pagination, _filters, sorter) => {
    const fieldKey = sorter?.columnKey || sorter?.field;
    if (!fieldKey) return;

    if (!sorter.order) {
      setSort({ field: null, order: null });
      return;
    }
    setSort({
      field: fieldKey,
      order: sorter.order === "ascend" ? "ASC" : "DESC",
    });
  };

  const orgRolesFilter = [
    { id: "systemAdmin", name: "System admin" },
    { id: "admin", name: "Organization admin" },
    // { id: "projectAdmin", name: "Project admin" },
    // { id: "projectManager", name: "Site manager" },
    { id: "user", name: "User" },
  ];

  const projectRolesFilter = assignableRoles.map((role) => ({ id: role.id, name: role.name }));

  const rolesFilter = isOrgScope ? orgRolesFilter : projectRolesFilter;

  const rolesFilterName = isOrgScope ? "Organization role" : "Project role";

  const statuses = [
    { id: "confirmed", name: "Active" },
    { id: "invited", name: "Invited" },
    { id: "inviteError", name: "Invitation error" },
    { id: "blocked", name: "Blocked" },
    { id: "inactive", name: "Inactive" },
  ];

  const updateFilterValue = (value) => setFilters(value);

  const handleBulkDelete = useCallback(async () => {
    if (selectedKeys.length === 0 || isBulkDeleting) return;

    setBulkDeleting(true);

    try {
      const resp = await ApiUsersManagement.removeBulk(selectedKeys);
      const { deletedIds = [], failed = [] } = resp?.data || {};
      const failedIds = failed.map((f) => f.id);

      if (deletedIds.length) {
        const plural = deletedIds.length > 1;
        openNotification?.({
          type: "success",
          message: (
            <p>
              {deletedIds.length} {plural ? "users were" : "user was"} deleted
            </p>
          ),
        });
      }

      if (failedIds.length) {
        openNotification?.({
          type: "error",
          message: (
            <p>
              Unable to delete {failedIds.length}{" "}
              {failedIds.length > 1 ? "users" : "user"}
            </p>
          ),
        });
      }

      if (!failedIds.length) {
        clearSelection();
      } else {
        setSelectedKeys(failedIds);
      }

      await resetAndFetch();
    } catch (e) {
      console.error(e);
      openNotification?.({
        type: "error",
        message: <p>Failed to delete users</p>,
      });
    } finally {
      setBulkDeleting(false);
    }
  }, [
    selectedKeys,
    isBulkDeleting,
    openNotification,
    clearSelection,
    setSelectedKeys,
    resetAndFetch,
  ]);

  // Rows are only selected to be deleted together.
  const tableColumnsWithSelection = useMemo(
    () => (canDelete ? [getSelectionColumn(), ...tableColumns] : tableColumns),
    [getSelectionColumn, tableColumns, canDelete]
  );

  return (
    <Body
      header={headerProps}
      filters={
        <DropdownFilters
          filtersBy={[
            { name: rolesFilterName, id: "roles", children: rolesFilter },
            { name: "Status", id: "status", children: statuses },
            ...(sitesEnabled && scope === "project"
              ? [{ name: "Sites", id: "siteIds", children: sitesList }]
              : []),
          ]}
          filter={initialFilters}
          updateFilterValue={updateFilterValue}
        />
      }
    >
    <InfinityTable
      rowTestID={(user) => `user-list-row-${user.id}`}
      data={users}
      fetchData={fetchNext}
      hasMore={hasMore}
      tableColumns={tableColumnsWithSelection}
      isLoading={isLoading}
      onHandleRowClick={openUserProfile}
      onChange={handleTableChange}
      containerRef={tableRef}
      after={
        <BulkSelectionBar
          left={bulkLeft}
          count={selectedKeys.length}
          onClear={clearSelection}
          onDelete={handleBulkDelete}
          loading={isBulkDeleting}
        />
      }
    />
    </Body>
  );
};

export default UserManagament;
